import React, { useState, useEffect } from 'react';
import Star from './star.jsx';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import '../app.css';
import supabase from '../supabase.js';
import finalglobe from '../assets/finalglobe.svg';
import finalmail from '../assets/finalmail.svg';
import defaultbusinessimg from '../assets/defaultBusiness.svg';

export default function Company({
  img,
  title,
  stars,
  trustscore,
  reviews,
  cat,        // From 'bank' table
  category,   // From 'businesses' table
  phone,
  address,
  country,
  isVerif,
  about,
  email,
  text,
  reviewList,
  date,
  www,
  comps,
  sorting,
  newSort,
}) {
  const navigate = useNavigate();

  // 1. UNIFY CATEGORY: Solve the "toLowerCase" crash
  const displayCategory = (cat || category || "General").toString();
  
  // 2. SAFE URL FORMATTING
  const modifiedwww = www?.replace(/^(https?:\/\/)?(www\.)?/, '');

const onCompanyNavigate = () => {
    // 1. Pick the one that isn't undefined
    const validCategory = cat || category;

    if (!validCategory) {
      console.warn("No category found for company:", title);
    }

    // 2. Format safely
    const formattedCat = (validCategory || "general").toLowerCase();
    
    // 3. Navigate
    const url = `/categories/${formattedCat}/${modifiedwww}`;
    
    setTimeout(() => {
      navigate(url);
      window.scrollTo(0, 0);
    }, 300);
  };

  // --- REVIEW CALCULATION LOGIC ---
  const [companyReviews, setCompanyReviews] = useState([]);

  useEffect(() => {
    const fetchCompanyReviews = async () => {
      if (!www) return;
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('company', www)
        .eq('is_approved', true);

      if (!error) setCompanyReviews(data || []);
    };
    fetchCompanyReviews();
  }, [www]);

  const toNumber = (v) => typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
  
  const originalReviews = toNumber(reviews) || 0;
  const fetchedReviews = companyReviews.length;
  const fullReviews = originalReviews + fetchedReviews;

  const fetchedStarSum = companyReviews.reduce((acc, r) => acc + Number(r.stars || 0), 0);
  const currentTrustscore = Number(trustscore) || 0;
  const currentTrustscoreWeightedSum = currentTrustscore * originalReviews;

  let finalTrustscore = fullReviews === 0 
    ? 0 
    : ((currentTrustscoreWeightedSum + fetchedStarSum) / fullReviews).toFixed(1);

  let userCalculatedScore = fetchedReviews > 0 
    ? (fetchedStarSum / fetchedReviews).toFixed(1) 
    : 0;

  // --- STAR UI HELPERS ---
  const customRound = (rating) => {
    const rounded = parseFloat(rating);
    if (isNaN(rounded)) return 0;
    const decimal = rounded % 1;
    if (decimal < 0.25) return Math.floor(rounded);
    if (decimal < 0.75) return Math.floor(rounded) + 0.5;
    return Math.ceil(rounded);
  };

  const getStarColor = (rounded) => {
    const colors = {
      5: 'bg-five', 4.5: 'bg-fourhalf', 4: 'bg-four',
      3.5: 'bg-threehalf', 3: 'bg-three', 2.5: 'bg-twohalf',
      2: 'bg-two', 1.5: 'bg-onehalf', 1: 'bg-one'
    };
    return colors[rounded] || 'bg-gray-300';
  };

  return (
    <div className='w-full flex flex-col max-w-[355px] xl:max-w-[650px] 2xl:max-w-[830px] xs:max-w-full mx-auto xs:mx-0 rounded-[4px] p-[16px] xs:p-0 xs:px-[16px] xs:pt-[16px] xs:pb-[10px] bg-white h-auto transition duration-500 hover:shadow-2xl'>
      
      <div onClick={onCompanyNavigate} className='flex flex-row gap-[18px] xl:gap-[38px] cursor-pointer'>
        <div className='w-[70px] h-[70px] justify-center flex items-center bg-white ip:w-[70px] ip:h-[70px] lg:w-[85px] lg:h-[85px] xl:w-[130px] xl:h-[130px] bg-black overflow-hidden'>
          {!img ? (
            <div className='w-full flex items-center justify-center h-full bg-gray-300'>
              <span className='uppercase font-poppins font-regular text-[42px]'>{title ? title[0] : '?'}</span>
            </div>
          ) : (
            <LazyLoadImage 
              src={img} 
              alt={title} 
              effect="blur"
              className="w-auto h-auto max-h-[120px]" 
              onError={(e) => {
                e.target.onerror = null; 
                e.target.src = defaultbusinessimg;
              }}
            />
          )}
        </div>

        <div className='flex flex-col xl:gap-0 xl:justify-between gap-[4px] xs:gap-[3px]'>
          <div className='flex flex-row gap-[10px] '>
            <h1 className='text-[12px] xs:text-[14px] lg:text-[16px] lg:-mt-[3px] -mt-[2px] text-[#1A1A1A] font-poppins font-semibold'>{title || "Loading..."}</h1>
          </div>
          <span className='font-poppins text-[10px] xl:text-[14px] lg:text-[12px] -mt-[2px] text-[#1A1A1A] opacity-86'>www.{modifiedwww}</span>
          
          <div className='flex flex-row xs:flex-col gap-[5px] ip:mt-[5px] xs:mt-[2px]'>
            <div className={`${fullReviews === 0 ? "hidden" : "flex"} gap-[2.2px] -mt-[3px] xl:gap-[3.6px] xs:gap-[2.2px]`}>
              {Array(5).fill().map((_, i) => {
                const starValue = fetchedReviews > 0 
                  ? customRound(parseFloat(userCalculatedScore)) 
                  : customRound(parseFloat(finalTrustscore));

                const isFullStar = i < Math.floor(starValue);
                const isHalfStar = i === Math.floor(starValue) && starValue % 1 >= 0.5;

                return (
                  <Star
                    boxwidth={'w-[15px] ip:w-[17px] xs:w-[19px] xl:w-[32px]'}
                    boxheight={'h-[15px] ip:h-[17px] xs:h-[19px] xl:h-[32px]'}
                    width={'w-[9px] ip:w-[11px] xs:w-[13px] xl:w-[18px]'}
                    key={i}
                    bgColor={isFullStar ? getStarColor(starValue) : 'bg-gray-300'}
                    half={isHalfStar}
                    halfStar={isHalfStar ? getStarColor(starValue) : 'bg-gray-300' }
                  />
                );
              })}
            </div>

            <div className='ml-[25px] xs:ml-0 xs:flex mt-[5px] xs:mt-0 flex-row items-center gap-[10px] hidden'>
              <h2 className='text-[10px] lg:text-[14px] font-poppins font-regular text-[#1A1A1A] opacity-80'>Manifestscore {trustscore}</h2>
              <div className={`w-[2.4px] h-[2.4px] ${fullReviews === 0 ? "hidden" : "flex"} rounded-full bg-black opacity-60`}></div>
              <h2 className='text-[10px] lg:text-[14px] font-poppins font-regular text-[#1A1A1A] opacity-80 '>{reviews} Testimonials</h2>
            </div>
          </div>


        </div>
      </div>

      <div className='w-full h-[0.5px] bg-[#1a1a1a] opacity-25 mt-[16px]'></div>

      <div className='flex flex-row pt-[10px] items-center justify-start gap-[35px] ip:justify-start ip:gap-[20px]'>
        <div className='flex flex-row items-center gap-[10px]'>
          <img onClick={(e) => { e.stopPropagation(); window.open(`https://${www}`, '_blank'); }} className='w-[24px] xl:w-[28px] cursor-pointer' src={finalglobe} alt="Website" />
          <img onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${email}`; }} className='w-[24px] xl:w-[28px] cursor-pointer' src={finalmail} alt="Email" />
        </div>

        <div className='flex flex-row items-center gap-[10px]'>
          <span className='text-[10px] text-[#1a1a1a] xl:text-[12px] opacity-80 font-poppins font-light'>{country || "Global"}</span>
          <div className='bg-black w-[2.3px] h-[2.3px] rounded-full opacity-40'></div>
          <span className='text-[10px] text-[#1a1a1a] xl:text-[12px] opacity-80 font-poppins font-light'>{displayCategory}</span>
        </div>
      </div>
    </div>
  );
}